import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Save } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORIES = ['Acheteur', 'Vendeur', 'Aménageur', 'Promoteur', 'Investisseur'];

const ContactNew = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    nom: '',
    prenoms: '',
    entreprise: '',
    email: '',
    telephone: '',
    source: '',
    categorie: ''
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.nom || !formData.prenoms || !formData.telephone || !formData.categorie) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/contacts`, formData, {
        withCredentials: true
      });
      toast.success('Contact créé avec succès');
      navigate('/contacts');
    } catch (error) {
      console.error('Error creating contact:', error);
      toast.error('Erreur lors de la création du contact');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-new-page">
      <div className="mb-8">
        <Button
          variant="ghost"
          onClick={() => navigate('/contacts')}
          className="mb-4"
          data-testid="back-button"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Retour
        </Button>
        <h1 className="text-3xl font-bold text-[#002255] mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Nouveau Contact
        </h1>
        <p className="text-gray-600">Ajoutez un nouveau contact à votre CRM</p>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200/40 p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Label htmlFor="nom">Nom *</Label>
              <Input
                id="nom"
                value={formData.nom}
                onChange={(e) => handleChange('nom', e.target.value)}
                placeholder="Nom de famille"
                required
                data-testid="input-nom"
              />
            </div>

            <div>
              <Label htmlFor="prenoms">Prénoms *</Label>
              <Input
                id="prenoms"
                value={formData.prenoms}
                onChange={(e) => handleChange('prenoms', e.target.value)}
                placeholder="Prénoms"
                required
                data-testid="input-prenoms"
              />
            </div>

            <div>
              <Label htmlFor="telephone">Téléphone *</Label>
              <Input
                id="telephone"
                type="tel"
                value={formData.telephone}
                onChange={(e) => handleChange('telephone', e.target.value)}
                placeholder="+225 XX XX XX XX XX"
                required
                data-testid="input-telephone"
              />
            </div>

            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                placeholder="email@exemple.com"
                data-testid="input-email"
              />
            </div>

            <div>
              <Label htmlFor="entreprise">Entreprise</Label>
              <Input
                id="entreprise"
                value={formData.entreprise}
                onChange={(e) => handleChange('entreprise', e.target.value)}
                placeholder="Nom de l'entreprise"
                data-testid="input-entreprise"
              />
            </div>

            <div>
              <Label htmlFor="source">Source</Label>
              <Input
                id="source"
                value={formData.source}
                onChange={(e) => handleChange('source', e.target.value)}
                placeholder="Réseau social, Référence, etc."
                data-testid="input-source"
              />
            </div>

            <div className="md:col-span-2">
              <Label htmlFor="categorie">Catégorie *</Label>
              <Select 
                value={formData.categorie} 
                onValueChange={(value) => handleChange('categorie', value)}
              >
                <SelectTrigger data-testid="select-categorie">
                  <SelectValue placeholder="Sélectionnez une catégorie" />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(cat => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex items-center gap-4 mt-8">
            <Button
              type="submit"
              disabled={loading}
              className="bg-[#002255] hover:bg-[#002255]/90"
              data-testid="submit-contact-button"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Création...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Créer le contact
                </>
              )}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/contacts')}
            >
              Annuler
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default ContactNew;
